import {useState, useRef, useEffect} from 'react';
import type {AttachedFile} from './types';

export interface UseChatAttachmentsReturn {
	attachedFile: AttachedFile | null;
	clearAttachment: () => void;
	attachDropdownOpen: boolean;
	setAttachDropdownOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
	attachLoading: boolean;
	attachError: string | null;
	fileInputRef: React.RefObject<HTMLInputElement>;
	attachBtnRef: React.RefObject<HTMLDivElement>;
	handleLocalFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleDrivePick: (data: Record<string, unknown>) => Promise<void>;
}

export function useChatAttachments(): UseChatAttachmentsReturn {
	const [attachedFile, setAttachedFile] = useState<AttachedFile | null>(null);
	const [attachDropdownOpen, setAttachDropdownOpen] = useState(false);
	const [attachLoading, setAttachLoading] = useState(false);
	const [attachError, setAttachError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const attachBtnRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!attachDropdownOpen) return;
		const handleOutside = (e: MouseEvent) => {
			if (attachBtnRef.current && !attachBtnRef.current.contains(e.target as Node)) {
				setAttachDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, [attachDropdownOpen]);

	const handleLocalFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;

		const ext = file.name.toLowerCase();
		if (!ext.endsWith('.pdf') && !ext.endsWith('.docx')) {
			setAttachError('Only PDF and DOCX files are supported.');
			setTimeout(() => setAttachError(null), 4000);
			return;
		}

		setAttachLoading(true);
		setAttachDropdownOpen(false);
		try {
			const dataBase64 = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					const result = reader.result as string;
					resolve(result.split(',')[1]);
				};
				reader.onerror = () => reject(new Error('Failed to read file'));
				reader.readAsDataURL(file);
			});
			setAttachedFile({filename: file.name, mimeType: file.type, dataBase64});
		} catch {
			setAttachError('Failed to read file.');
			setTimeout(() => setAttachError(null), 4000);
		} finally {
			setAttachLoading(false);
		}
	};

	const handleDrivePick = async (data: Record<string, unknown>) => {
		const docs = data.docs as Array<{id: string; name: string; mimeType: string}> | undefined;
		if (data.action !== 'picked' || !docs?.length) return;
		setAttachDropdownOpen(false);
		const doc = docs[0];
		const fileId: string = doc.id;
		const filename: string = doc.name;
		const mimeType: string = doc.mimeType;
		const accessToken: string = (data.accessToken as string) ?? '';

		if (!mimeType.includes('pdf') && !mimeType.includes('wordprocessingml')) {
			setAttachError('Only PDF and DOCX files are supported from Google Drive.');
			setTimeout(() => setAttachError(null), 4000);
			return;
		}

		setAttachLoading(true);
		try {
			const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
				headers: {Authorization: `Bearer ${accessToken}`},
			});
			if (!res.ok) throw new Error(`Drive API error: ${res.status}`);
			const blob = await res.blob();
			const dataBase64 = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve((reader.result as string).split(',')[1]);
				reader.onerror = () => reject(new Error('Failed to encode file'));
				reader.readAsDataURL(blob);
			});
			setAttachedFile({filename, mimeType, dataBase64});
		} catch (err: unknown) {
			setAttachError(err instanceof Error ? err.message : 'Failed to fetch file from Google Drive.');
			setTimeout(() => setAttachError(null), 4000);
		} finally {
			setAttachLoading(false);
		}
	};

	return {
		attachedFile,
		clearAttachment: () => setAttachedFile(null),
		attachDropdownOpen,
		setAttachDropdownOpen,
		attachLoading,
		attachError,
		fileInputRef,
		attachBtnRef,
		handleLocalFile,
		handleDrivePick,
	};
}
